import { apiBase } from '../region'
import { sessionToken } from '../auth'
import { handleRejection } from '../session-state'
import type { Storage } from '../../platform/storage'
import { useKid } from '../../platform/web'
import { KidStorage, currentKid, kids, saveKids } from '../../stores/children'
import { adoptServerId, mergeDuplicateKids } from '../../stores/kids-boot'
import { announceState, program, rememberTourSeen } from '../../state/store'
import { apply } from './apply'
import { localSnapshot, type SyncAnswer } from './payload'

/**
 * Сам обмен: сходить на сервер и разложить ответ.
 *
 * Без него аккаунт почти ничего не значил: профиль, правки «Моего дня», история окон
 * и заметки жили только в памяти браузера, и обещание «расписание не потеряется при
 * смене телефона» не выполнялось.
 */

async function call(path: string, body?: unknown): Promise<SyncAnswer> {
  const token = sessionToken()
  if (token === null) throw new Error('Нет ключа сессии')
  const response = await fetch(`${apiBase()}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'content-type': 'application/json', authorization: `Session ${token}` },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })
  if (!response.ok) {
    // 401 и 410 — не «нет связи», а ответ сервера про аккаунт: он разбирается отдельно
    await handleRejection(response.status)
    throw new Error(`${path} → ${response.status}`)
  }
  return (await response.json()) as SyncAnswer
}

/** Отправляет список малышей и возвращает ответ сервера. */
async function callMany(children: unknown[]): Promise<{ children: SyncAnswer[]; tourSeen?: boolean; serverNow: number } | null> {
  const token = sessionToken()
  if (token === null) return null
  const response = await fetch(`${apiBase()}/sync`, {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Session ${token}` },
    body: JSON.stringify({ children, since: 0 }),
  })
  if (!response.ok) {
    await handleRejection(response.status)
    throw new Error(`/sync → ${response.status}`)
  }
  return (await response.json()) as { children: SyncAnswer[]; tourSeen?: boolean; serverNow: number }
}

/** Общее хранилище устройства: список малышей лежит там, а не под приставкой. */
function deviceStorageOf(storage: KidStorage): KidStorage {
  return storage
}

/**
 * Обмен, когда малышей может быть несколько: шлём всех, кого знаем, и раскладываем ответ
 * по их хранилищам. Малыш, заведённый на другом устройстве, приезжает в ответе — его
 * данные кладём под новой приставкой и добавляем в список.
 *
 * Список может быть и пустым — так выглядит новый телефон после входа. Тогда посылка
 * пустая, а сервер присылает всех малышей счёта: их и берём, первого показываем.
 */
async function syncKids(storage: KidStorage, onApplied?: (() => void) | null): Promise<boolean> {
  const mine = kids.value
  const payload = []
  for (const kid of mine) {
    const snapshot = await localSnapshot(storage.forKid(kid.id), kid.id)
    if (snapshot !== null) payload.push(snapshot)
  }
  const answer = (await callMany(payload)) ?? null
  if (answer === null) return false
  if (answer.tourSeen === true) await rememberTourSeen()

  let list = mine
  let переехали = false
  // Сервер постарше отвечает одним малышом, а не списком: принимаем и так
  const приехали = Array.isArray(answer.children)
    ? answer.children
    : ((answer as unknown as SyncAnswer).child ? [answer as unknown as SyncAnswer] : [])
  for (const one of приехали) {
    // Сервер всегда присылает малыша, но по типу ответа он может быть пустым
    if (one.child === null) continue
    // Хозяин номера — счёт: своё временное имя ячейки меняем на выданное сервером
    const id = one.child.serverId ?? one.child.clientId
    const отправляли = one.requestedClientId ?? one.child.clientId
    if (отправляли !== id && list.some((k) => k.id === отправляли)) {
      await adoptServerId(отправляли, id, program.value)
      list = kids.value
      // Расписание под новым ключом нужно завести сразу: старое мы только что сняли,
      // а идущее окно иначе осталось бы без напоминаний до следующего действия
      переехали = true
    }
    await apply(storage.forKid(id), { ...one, serverNow: answer.serverNow })
    if (!list.some((k) => k.id === id)) list = [...list, { id, name: one.child.name }]
  }
  // Текущего может не быть вовсе: на новом телефоне показываем того, с кем работали
  // последним, иначе приложение считает, что малыша нет, и зовёт заполнить анкету заново
  const current = currentKid.value ?? свежайший(приехали) ?? list[0]?.id ?? null
  const появился = current !== null && current !== currentKid.value
  if (list !== mine || появился) {
    await saveKids(deviceStorageOf(storage), list, current)
    if (появился) {
      useKid(current)
      onApplied?.()
    }
  }
  // Та же анкета, заведённая дважды, приезжает сюда двумя малышами: пустую сливаем
  // с той, где история, иначе в списке висят два одинаковых «Паши» (12.10.2026)
  if (переехали) await announceState()
  if ((await mergeDuplicateKids()) !== null) onApplied?.()
  return true
}

/**
 * Кем из малышей счёта встретить новый телефон: тем, о ком записи свежее. Порядок
 * заведения тут плохой советчик — первым может оказаться старший, которого уже не ведут.
 */
function свежайший(приехали: SyncAnswer[]): string | null {
  let лучший: { id: string; at: number } | null = null
  for (const one of приехали) {
    if (one.child === null) continue
    const at = Math.max(
      one.child.updatedAtMs,
      ...one.events.map((e) => e.atMs),
      ...one.windows.map((w) => w.startedAtMs),
    )
    if (лучший === null || at > лучший.at) лучший = { id: one.child.clientId, at }
  }
  return лучший?.id ?? null
}

/**
 * Полный обмен: отдать своё, забрать общее. Возвращает false, если обмен не состоялся —
 * нет входа или не было сети. Это не ошибка: приложение работает и без сервера.
 *
 * `onApplied` зовут, только если сервер действительно что-то изменил. Раньше это была
 * переменная модуля, которую ставил `bindSync`; теперь она приезжает доводом, чтобы
 * очередь отправки и сам обмен не ссылались друг на друга по кругу.
 */
export async function pullKidsOnStart(storage: Storage): Promise<void> {
  if (sessionToken() === null || !(storage instanceof KidStorage) || kids.value.length > 0) return
  try {
    await syncKids(storage)
  } catch {
    // Сети нет — покажем то, что есть на устройстве
  }
}

export async function syncNow(storage: Storage, onApplied?: (() => void) | null): Promise<boolean> {
  if (sessionToken() === null) return false
  try {
    // Пустой список — не повод идти старой дорогой: именно там малыш со счёта ложился
    // в безымянную ячейку и на устройстве так и не заводился (случай 12.10.2026)
    if (storage instanceof KidStorage) return await syncKids(storage, onApplied)
    const before = await localSnapshot(storage)
    const answer = before === null ? await call('/sync') : await call('/sync', { ...before, since: 0 })
    if (answer.tourSeen === true) await rememberTourSeen()
    await apply(storage, answer)
    // Перечитывать экраны стоит, только если сервер действительно что-то изменил:
    // иначе каждое сохранение дёргало бы интерфейс на ровном месте.
    const after = await localSnapshot(storage)
    if (JSON.stringify(before) !== JSON.stringify(after)) onApplied?.()
    return true
  } catch {
    return false
  }
}
